import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Flag, Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    if (!email || !email.trim()) {
      setError('Please enter your email address.');
      setLoading(false);
      return;
    }

    console.log("Submitting password reset for:", email);

    try {
      const functions = getFunctions();
      const sendCustomPasswordReset = httpsCallable(functions, 'sendCustomPasswordReset');
      const result = await sendCustomPasswordReset({ email: email.trim() });

      if (result.data && result.data.success) {
        setSuccess(true);
        setEmail('');
        
        // Store the full message (including attempt warning)
        setSuccessMessage(result.data.message || 'Check your inbox for the password reset link.');
        setError(''); // Clear any previous errors
      } else {
        setError(result.data && result.data.message
          ? result.data.message
          : 'Failed to send reset email. Please try again.');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      if (err.code === 'resource-exhausted' && err.message) {
        setError(err.message);
      } else if (err.code === 'functions/not-found') {
        setError('Password reset service unavailable. Please try again later.');
      } else if (err.code === 'functions/invalid-argument') {
        setError('Please enter a valid email address.');
      } else if (err.code === 'functions/internal') {
        setError('Failed to send reset email. Please try again.');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div style={{ width: '400px' }}>
        <Card className="w-full shadow-lg">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-2">
              <Flag className="h-12 w-12 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-center">Reset Password</CardTitle>
            <p className="text-sm text-gray-500 text-center">
              Enter your email and we'll send you a reset link
            </p>
          </CardHeader>
          <CardContent className="px-6">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded flex items-start gap-2">
                <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {success ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <p className="text-green-700 font-medium">Reset email sent!</p>
                  </div>
                  <p className="text-green-600 text-sm mt-1 ml-7">
                    {successMessage}
                  </p>
                </div>
                <Link to="/login">
                <Button className="w-full bg-gray-900 hover:bg-gray-800 text-white py-2 rounded-md transition-colors">
                    Back to Login
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700" htmlFor="email">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white py-2 rounded-md transition-colors"
                >
                  {loading ? 'Sending...' : 'Send Reset Email'}
                </Button>
              </form>
            )}

            {!success && (
              <div className="mt-6 text-center">
                <Link to="/login" className="text-blue-600 hover:text-blue-700 text-sm inline-flex items-center gap-1">
                  <ArrowLeft className="h-4 w-4" />
                  Back to login
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default ForgotPassword;
